import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { AiOutlineMenu } from "react-icons/ai";
import { SlClose } from "react-icons/sl";
import { FaFacebook } from "react-icons/fa";
import colors from "../../styles/colors";
import NavModal from "./NavModal";
import config from "../../utils/config";
import LanguageSelector from "./LanguageSelector";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [isAtTop, setIsAtTop] = useState(true);
  const controls = useAnimation();
  const location = useLocation();
  const [isHoveredMenu, setIsHoveredMenu] = useState(false);
  const [isHoveredFb, setIsHoveredFb] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const currentTab = () => {
    const path = location.pathname;
    if (path.startsWith("/products")) {
      return "products";
    }
    return path === "/" ? "home" : path.slice(1);
  };

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    setIsAtTop(currentScrollY === 0);
    if (currentScrollY > lastScrollY + 10) {
      setScrollDirection("down");
    } else if (currentScrollY < lastScrollY - 10) {
      setScrollDirection("up");
    }
    setLastScrollY(currentScrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  useEffect(() => {
    if (!isOpen) {
      if (scrollDirection === "down") {
        controls.start({
          y: "-100%",
          transition: { duration: 0.4, ease: "easeInOut" }, // Sima eltűnés
        });
      } else {
        controls.start({
          y: "0%",
          transition: { duration: 0.4, ease: "easeInOut" }, // Sima megjelenés
        });
      }
    }
  }, [scrollDirection, controls, isOpen]);

  return (
    <motion.nav
      className={`fixed top-0 left-0 w-full z-50 bg-black transition-colors duration-500 ${
        isAtTop ? "bg-opacity-0" : "bg-opacity-50"
      } hover:bg-opacity-100`}
      initial={{ y: "0%" }}
      animate={controls}
    >
      <div className="h-24 flex justify-between items-center px-6 max-w-7xl mx-auto">
        {/* Left - Brand (Always visible) */}
        <div
          className={`flex items-center ${
            isOpen ? "z-[100]" : "z-[50]"
          }`}
          style={{ color: colors.lightCream }}
        >
          <Link to="/" onClick={() => setHoveredLink(null)}>
            <img
              src="/assets/images/logo/uniart-light.png"
              alt="Uniart logo"
              className="w-auto max-w-[90px] sm:max-w-[75px] lg:max-w-[120px] h-auto py-2 object-contain"
            />
          </Link>
        </div>

        {/* Right - Language Selector, Facebook, and Hamburger Menu */}
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Language Selector */}
          <LanguageSelector />

          {/* Facebook Icon */}
          <a
            href={config.facebookPage}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaFacebook
              className="text-4xl sm:text-4xl cursor-pointer transition-colors duration-300"
              style={{
                color: isHoveredFb ? colors.primaryGreen : colors.lightCream,
              }}
              onMouseEnter={() => setIsHoveredFb(true)}
              onMouseLeave={() => setIsHoveredFb(false)}
            />
          </a>

          {/* Hamburger Menu */}
          <button
            onClick={() => {
              setIsOpen(!isOpen);
              setIsHoveredMenu(false); // Reseteljük a hover state-et, amikor kattintasz rá
            }}
            className="z-50"
          >
            {isOpen ? (
              <SlClose
                className="h-8 w-8 sm:h-10 sm:w-10 transition-colors duration-300"
                style={{
                  color: isHoveredMenu
                    ? colors.primaryGreen
                    : colors.lightCream,
                }}
                onMouseEnter={() => setIsHoveredMenu(true)}
                onMouseLeave={() => setIsHoveredMenu(false)}
                aria-hidden="true"
              />
            ) : (
              <AiOutlineMenu
                className="h-8 w-8 sm:h-10 sm:w-10 transition-colors duration-300"
                style={{
                  color: isHoveredMenu
                    ? colors.primaryGreen
                    : colors.lightCream,
                }}
                onMouseEnter={() => setIsHoveredMenu(true)}
                onMouseLeave={() => setIsHoveredMenu(false)}
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </div>
      {/* Modal */}
      {isOpen && (
        <NavModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          hoveredLink={hoveredLink}
          setHoveredLink={setHoveredLink}
          currentTab={currentTab}
        />
      )}
    </motion.nav>
  );
};

export default Navbar;
